import React, { useState, useEffect, useRef, useCallback } from 'react';
import './tmi.css';
import './MasterProjects.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import {
    Grid,
    GridItemChangeEvent,
    GridCellProps,
    GridColumn,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type MasterProjectsProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: MasterProjectsState;
    onSaveState?: (menuItemId: number, state: MasterProjectsState) => void;
};

interface LookupListItem {
    value: string;
    display: string;
}

interface MasterProjectRow {
    projectID: number;
    projectCode: string;
    projectName: string;
    projectDesc: string | null;
    projectDataFolder: string;
    sqlDatabaseName: string | null;
    sqlDatabaseServer: string | null;
    isActive: boolean;
    useMultiFactorAuthentication: boolean;
    runTcBImporterService: boolean;
    runTcBSearchQueue: boolean;
    processInventoryScans: boolean;
    apiUrl: string | null;
    backgroundImg: string | null;
    backgroundImgOpacity: number | null;
    databaseTimeZone: string | null;
}



interface MasterProjectsState {
    data: MasterProjectRow[];
    loading: boolean;
    errorMsg: string;
    sort: SortDescriptor[];
    filterTexts: { [key: string]: string };
    headerFocusField?: string | null;
    editItemId: number | null;
    originalItem: MasterProjectRow | null;
    selectedIds: Set<number>;
    menuDataItem: MasterProjectRow | null;
    menuAnchor: HTMLElement | null;
    menuAnchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
    menuPosition: { left: number; top: number } | null;
    newMasterProjectDlgOpen: boolean;
    newMasterProjectDlgSaving: boolean;
    newMasterProjectDlgError: string;
    newMasterProjectDlgCode: string;
    newMasterProjectDlgName: string;
    newMasterProjectDlgDesc: string;
    newMasterProjectDlgDataFolder: string;
    newMasterProjectDlgSqlDatabaseName: string;
    newMasterProjectDlgSqlDatabaseServer: string;
    newMasterProjectDlgIsActive: boolean;
    newMasterProjectDlgImporter: boolean;
    newMasterProjectDlgSearchQueue: boolean;
    newMasterProjectDlgInventoryScans: boolean;
    newMasterProjectDlgApiUrl: string;
    newMasterProjectDlgBgOpacity: number;
    newMasterProjectDlgTimeZone: string;
    deleteConfirmItem: MasterProjectRow | null;
    deleteInProgress: boolean;
    deleteErrorMsg: string;
    usersDropdown: LookupListItem[];
    usersForProjectDlgOpen: boolean;
    usersForProjectProject: MasterProjectRow | null;
    usersForProjectSelected: Set<string>;
    usersForProjectDlgLoading: boolean;
    usersForProjectDlgError: string;
    usersForProjectDlgFilterText: string;
    backgroundImgDlgOpen: boolean;
    backgroundImgDlgProject: MasterProjectRow | null;
    backgroundImgDlgLoading: boolean;
    backgroundImgDlgError: string;
    globalMfaChallengeIntervalDays: number | '';
    globalMfaCodeExpiryMinutes: number | '';
    globalMfaSettingsLoading: boolean;
    globalMfaSettingsSaving: boolean;
    globalMfaSettingsError: string;
}

const DATA_ITEM_KEY = 'projectID';
const EDIT_FIELD = 'inEdit';
const SELECTED_FIELD = 'selected';

const MasterProjects: React.FC<MasterProjectsProps> = (props) => {
    const STORAGE_KEY = 'MasterProjects_state_v1';
    const MENU_FLIP_OFFSET = 8;
    const menuBlurTimeoutRef = useRef<any>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);




    // attempt to restore saved UI state from sessionStorage
    const savedRaw = (() => {
        try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    const saved = savedRaw ? JSON.parse(savedRaw) : null;

    // When navigating away to another menu item and back, the parent App restores
    // the full previous state (including filters) via stateToLoad/onSaveState, so
    // the page doesn't need to refilter/reselect. On first visit (or a full page
    // reload) there is no stateToLoad, so fall back to the sessionStorage-seeded defaults.
    const [state, setState] = useState<MasterProjectsState>(props.stateToLoad ?? {
        data: [],
        loading: false,
        errorMsg: '',
        sort: saved?.sort ?? [{ field: 'projectCode', dir: 'asc' }],
        filterTexts: {},
        headerFocusField: null,
        editItemId: null,
        originalItem: null,
        selectedIds: new Set(saved?.selectedIds ?? []),
        menuDataItem: null,
        menuAnchor: null,
        menuAnchorRect: null,
        menuPosition: null,
        newMasterProjectDlgOpen: false,
        newMasterProjectDlgSaving: false,
        newMasterProjectDlgError: '',
        newMasterProjectDlgCode: '',
        newMasterProjectDlgName: '',
        newMasterProjectDlgDesc: '',
        newMasterProjectDlgDataFolder: '',
        newMasterProjectDlgSqlDatabaseName: '',
        newMasterProjectDlgSqlDatabaseServer: '',
        newMasterProjectDlgIsActive: true,
        newMasterProjectDlgImporter: true,
        newMasterProjectDlgSearchQueue: true,
        newMasterProjectDlgInventoryScans: false,
        newMasterProjectDlgApiUrl: '',
        newMasterProjectDlgBgOpacity: 100,
        newMasterProjectDlgTimeZone: '',
        deleteConfirmItem: null,
        deleteInProgress: false,
        deleteErrorMsg: '',
        usersDropdown: [],
        usersForProjectDlgOpen: false,
        usersForProjectProject: null,
        usersForProjectSelected: new Set(),
        usersForProjectDlgLoading: false,
        usersForProjectDlgError: '',
        usersForProjectDlgFilterText: '',
        backgroundImgDlgOpen: false,
        backgroundImgDlgProject: null,
        backgroundImgDlgLoading: false,
        backgroundImgDlgError: '',
        globalMfaChallengeIntervalDays: '',
        globalMfaCodeExpiryMinutes: '',
        globalMfaSettingsLoading: false,
        globalMfaSettingsSaving: false,
        globalMfaSettingsError: '',
    });

    // Keep latest state available to stable cell renderers without recreating them.
    const stateRef = useRef(state);
    stateRef.current = state;

    const saveState = useCallback(() => {
        try {
            const toSave = {
                sort: state.sort,
                selectedIds: Array.from(state.selectedIds ?? []),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        } catch (e) {
            // ignore storage errors
        }
    }, [state.sort, state.selectedIds]);

    useEffect(() => {
        fetchData();
        fetchUsersDropdown();
        fetchGlobalMfaSettings();
        return () => {
            // cleanup on unmount
            saveState();
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
            if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close menu when clicking outside of it
    useEffect(() => {
        if (!state.menuDataItem) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                closeRowMenu();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [state.menuDataItem]);

    const fetchGlobalMfaSettings = () => {
        setState(prev => ({ ...prev, globalMfaSettingsLoading: true, globalMfaSettingsError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetGlobalMfaSettings';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => {
                if (resp.status === 404) throw new Error('Endpoint not found (HTTP 404) - is the API up to date and running?');
                return resp.json();
            })
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, globalMfaSettingsLoading: false, globalMfaSettingsError: 'Error fetching MFA settings: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({
                        ...prev,
                        globalMfaChallengeIntervalDays: res.result.mfaChallengeIntervalDays,
                        globalMfaCodeExpiryMinutes: res.result.mfaCodeExpiryMinutes,
                        globalMfaSettingsLoading: false,
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, globalMfaSettingsLoading: false, globalMfaSettingsError: 'Error fetching MFA settings: ' + err.toString() }));
            });
    }

    const saveGlobalMfaSettings = () => {
        setState(prev => ({ ...prev, globalMfaSettingsSaving: true, globalMfaSettingsError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/UpdateGlobalMfaSettings';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                MFAChallengeIntervalDays: state.globalMfaChallengeIntervalDays === '' ? 30 : state.globalMfaChallengeIntervalDays,
                MFACodeExpiryMinutes: state.globalMfaCodeExpiryMinutes === '' ? 30 : state.globalMfaCodeExpiryMinutes,
            }),
        })
            .then(resp => {
                if (resp.status === 404) throw new Error('Endpoint not found (HTTP 404) - is the API up to date and running?');
                return resp.json();
            })
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, globalMfaSettingsSaving: false, globalMfaSettingsError: 'Error saving MFA settings: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, globalMfaSettingsSaving: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, globalMfaSettingsSaving: false, globalMfaSettingsError: 'Error saving MFA settings: ' + err.toString() }));
            });
    }

    const fetchData = () => {
        setState(prev => ({ ...prev, loading: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMasterProjects';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        setState(prev => ({ ...prev, data: res.results, loading: false }));
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        break;
                    default:
                        setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + res.callStatusMessage, loading: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + err.toString(), loading: false }));
            });
    }

    const fetchUsersDropdown = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMasterUsersForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && res.results) {
                    setState(prev => ({ ...prev, usersDropdown: res.results as LookupListItem[] }));
                }
            })
            .catch(err => {
                // Silently ignore errors fetching dropdown data
            });
    }




    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.sort && prev.sort.length > 0 ? prev.sort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, sort: newSort };
        });
    }, []);

    const handleHeaderSortClick = useCallback((field: string, e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('input, select, textarea, button')) return;
        toggleSort(field);
    }, [toggleSort]);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const val = e.target.value;
        setState(prev => ({ ...prev, filterTexts: { ...prev.filterTexts, [field]: val } }));
    }, []);


    const handleEnterEdit = (dataItem: MasterProjectRow) => {
        const original = state.data.find(d => d.projectID === dataItem.projectID);
        setState(prev => ({ ...prev, editItemId: dataItem.projectID, originalItem: original ? { ...original } : null }));
    }

    const handleItemChange = (e: GridItemChangeEvent) => {
        const { field, value, dataItem } = e;
        if (!field || field === 'projectID') return;

        // Validate backgroundImgOpacity: must be integer between 0 and 100
        let finalValue = value;
        if (field === 'backgroundImgOpacity') {
            const numVal = parseInt(String(value), 10);
            if (isNaN(numVal)) return;
            finalValue = Math.max(0, Math.min(100, numVal));
        }

        setState(prev => ({ ...prev, data: prev.data.map(item => item.projectID === dataItem.projectID ? { ...item, [field]: finalValue } : item) }));
    }

    const handleSave = (dataItem: MasterProjectRow) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMasterProject';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(dataItem),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, errorMsg: 'Error saving MasterProject: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, editItemId: null, originalItem: null, errorMsg: '' }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error saving MasterProject: ' + err.toString() }));
            });
    }

    const handleCancel = () => {
        setState(prev => {
            const originalItem = prev.originalItem;
            return {
                ...prev,
                data: originalItem ? prev.data.map(item => item.projectID === originalItem.projectID ? originalItem : item) : prev.data,
                editItemId: null,
                originalItem: null,
            };
        });
    }

    const handleSaveRef = useRef(handleSave);
    handleSaveRef.current = handleSave;
    const handleCancelRef = useRef(handleCancel);
    handleCancelRef.current = handleCancel;

    const handleEditorKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, dataItem: MasterProjectRow) => {
        e.stopPropagation();

        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveRef.current(dataItem);
            return;
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelRef.current();
        }
    }, [])


    const openDeleteConfirm = (dataItem: MasterProjectRow) => {
        setState(prev => ({ ...prev, deleteConfirmItem: dataItem, deleteErrorMsg: '' }));
    }

    const closeDeleteConfirm = () => {
        setState(prev => ({ ...prev, deleteConfirmItem: null, deleteInProgress: false, deleteErrorMsg: '' }));
    }
    const confirmDelete = () => {
        const { deleteConfirmItem } = state;
        if (!deleteConfirmItem) return;
        setState(prev => ({ ...prev, deleteInProgress: true, deleteErrorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeleteMasterProject';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(deleteConfirmItem.projectID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    closeDeleteConfirm();
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + res.callStatusMessage }));
                } else {
                    closeDeleteConfirm();
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + err.toString() }));
            });
    }



    const openRowMenu = (dataItem: MasterProjectRow, anchor: HTMLElement) => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        // Debug: log anchor and bounding rect to help diagnose positioning issues
        // (debug logs removed)
        // We'll measure the real menu before showing to avoid a visual jump.
        // Snapshot anchor bounding rect now (stabilizes values for measurement) and
        // set menuPosition to null to indicate "hidden-for-measure" state.
        try {
            const ar = anchor.getBoundingClientRect();
            const anchorRect = { left: Math.round(ar.left), top: Math.round(ar.top), right: Math.round(ar.right), bottom: Math.round(ar.bottom), width: Math.round(ar.width), height: Math.round(ar.height) };
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: anchorRect, menuPosition: null }));
        } catch {
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: null, menuPosition: null }));
        }
        // After rendering, log the popup node and positioning to diagnose placement
        // removed debug logging
    }

    const closeRowMenu = () => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null }));
    }

    // useCallback with an empty dependency array (reading editItemId via stateRef rather than
    // closing over `state` directly) keeps this handler's identity permanently stable across
    // renders, so the shared TextEditCell/NumberEditCell cell renderers that depend on it don't
    // get a new function identity on every keystroke.
    const handleRowClick = useCallback((projectID: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (stateRef.current.editItemId === projectID) return;
        setState(prev => {
            const newSelectedIds = new Set(prev.selectedIds);
            if (newSelectedIds.has(projectID)) {
                newSelectedIds.delete(projectID);
            } else {
                newSelectedIds.clear();
                newSelectedIds.add(projectID);
            }
            return { ...prev, selectedIds: newSelectedIds };
        });
    }, []);

    const onMenuFocus = () => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
    }

    const onMenuBlur = () => {
        menuBlurTimeoutRef.current = setTimeout(() => closeRowMenu(), 100);
    }

    // When menuAnchor exists but menuPosition is null, render the menu hidden
    // and measure its real size, then compute the final position and show it.
    useEffect(() => {
        if (!state.menuAnchor || state.menuPosition !== null) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const snap = state.menuAnchorRect;
                const actualAnchorRect = snap ? snap : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const menuW = Math.round(menuRect.width);
                const menuH = Math.round(menuRect.height);

                let left = Math.round(actualAnchorRect.left + scrollX);
                let top = Math.round(actualAnchorRect.bottom + scrollY);

                // Clamp horizontally
                if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                if (left < scrollX + 8) left = Math.round(scrollX + 8);

                // If not enough room below, flip above and anchor bottom-left to the button
                if (top + menuH > scrollY + winH) {
                    // Account for the anchor (button) height when flipping above so the
                    // menu bottom aligns with the top of the button.
                    const flipped = Math.round(actualAnchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    if (flipped >= scrollY + 8) top = flipped; else top = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    left = Math.round(actualAnchorRect.left + scrollX);
                    if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (left < scrollX + 8) left = Math.round(scrollX + 8);
                }

                setState(prev => ({ ...prev, menuPosition: { left, top } }));
            } catch (e) {
                // ignore measurement errors
            }
        }, 10);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    // Run measurement/flip when an anchor and a menuPosition exist (we may adjust based on real menu size)
    useEffect(() => {
        if (!state.menuAnchor || !state.menuPosition) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const snap = state.menuAnchorRect;
                const anchorRect = snap ? snap as any : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const menuW = menuRect.width;
                const menuH = menuRect.height;

                const belowTop = Math.round(anchorRect.bottom + scrollY);
                const anchorLeftAbs = Math.round(anchorRect.left + scrollX);

                let newLeft = state.menuPosition!.left;
                let newTop = state.menuPosition!.top;

                // If currently positioned below the anchor (top at/after belowTop) but it overflows the viewport bottom, flip above
                if (state.menuPosition!.top >= belowTop - 2 && (menuRect.bottom > winH)) {
                    // Anchor bottom-left should align with menu bottom-left: left = anchor.left
                    newLeft = anchorLeftAbs;
                    // Compute flipped top so menu bottom aligns with the top of the button
                    // (account for the button height so menu doesn't render too low).
                    newTop = Math.round(anchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    // Clamp horizontally if necessary
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                } else {
                    // Otherwise, ensure menu is clamped within viewport horizontally/vertically
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                    if (newTop + menuH > scrollY + winH) newTop = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    if (newTop < scrollY + 8) newTop = Math.round(scrollY + 8);
                }

                // Debugging info removed

                if (newLeft !== state.menuPosition!.left || newTop !== state.menuPosition!.top) {
                    setState(prev => ({ ...prev, menuPosition: { left: newLeft, top: newTop } }));
                }
            } catch (e) {
                // ignore measurement errors
            }
        }, 30);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    useEffect(() => {
        if (!state.menuDataItem) return;

        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as Node;
            // Check if click is outside the menu ref and outside the anchor button
            if (menuRef.current && !menuRef.current.contains(target) &&
                state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, [state.menuDataItem, state.menuAnchor]);

    const openNewMasterProjectDlg = () => {
        setState(prev => ({
            ...prev,
            newMasterProjectDlgOpen: true,
            newMasterProjectDlgSaving: false,
            newMasterProjectDlgError: '',
            newMasterProjectDlgCode: '',
            newMasterProjectDlgName: '',
            newMasterProjectDlgDesc: '',
            newMasterProjectDlgDataFolder: '',
            newMasterProjectDlgSqlDatabaseName: '',
            newMasterProjectDlgSqlDatabaseServer: '',
            newMasterProjectDlgIsActive: true,
            newMasterProjectDlgImporter: true,
            newMasterProjectDlgSearchQueue: true,
            newMasterProjectDlgInventoryScans: false,
            newMasterProjectDlgApiUrl: '',
            newMasterProjectDlgBgOpacity: 100,
            newMasterProjectDlgTimeZone: '',
        }));
    }

    const closeNewMasterProjectDlg = () => {
        setState(prev => ({ ...prev, newMasterProjectDlgOpen: false }));
    }

    const openNewProjectBasedOn = (baseProject: MasterProjectRow) => {
        closeRowMenu();
        setState(prev => ({
            ...prev,
            newMasterProjectDlgOpen: true,
            newMasterProjectDlgSaving: false,
            newMasterProjectDlgError: '',
            newMasterProjectDlgCode: `${baseProject.projectCode}_copy`,
            newMasterProjectDlgName: `${baseProject.projectName}_copy`,
            newMasterProjectDlgDesc: baseProject.projectDesc || '',
            newMasterProjectDlgDataFolder: baseProject.projectDataFolder,
            newMasterProjectDlgSqlDatabaseName: baseProject.sqlDatabaseName || '',
            newMasterProjectDlgSqlDatabaseServer: baseProject.sqlDatabaseServer || '',
            newMasterProjectDlgIsActive: baseProject.isActive,
            newMasterProjectDlgImporter: baseProject.runTcBImporterService,
            newMasterProjectDlgSearchQueue: baseProject.runTcBSearchQueue,
            newMasterProjectDlgInventoryScans: baseProject.processInventoryScans,
            newMasterProjectDlgApiUrl: baseProject.apiUrl || '',
            newMasterProjectDlgBgOpacity: baseProject.backgroundImgOpacity || 100,
            newMasterProjectDlgTimeZone: baseProject.databaseTimeZone || '',
        }));
    }

    const openUsersForProjectDialog = (project: MasterProjectRow) => {
        closeRowMenu();
        setState(prev => ({
            ...prev,
            usersForProjectDlgOpen: true,
            usersForProjectProject: project,
            usersForProjectSelected: new Set(),
            usersForProjectDlgLoading: true,
            usersForProjectDlgError: '',
            usersForProjectDlgFilterText: '',
        }));

        const url = props.userInf.currProject.apiUrl + `/api/TMI/GetProjectUsersForDropDown?projectId=${project.projectID}`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK' && res.results) {
                    const selected = new Set((res.results as LookupListItem[]).map(item => item.value));
                    setState(prev => ({
                        ...prev,
                        usersForProjectSelected: selected,
                        usersForProjectDlgLoading: false,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        usersForProjectDlgLoading: false,
                        usersForProjectDlgError: 'Failed to load users',
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    usersForProjectDlgLoading: false,
                    usersForProjectDlgError: 'Error loading users: ' + err.toString(),
                }));
            });
    }

    const closeUsersForProjectDialog = () => {
        setState(prev => ({
            ...prev,
            usersForProjectDlgOpen: false,
            usersForProjectProject: null,
            usersForProjectSelected: new Set(),
            usersForProjectDlgLoading: false,
            usersForProjectDlgError: '',
            usersForProjectDlgFilterText: '',
        }));
    }

    const toggleUserSelection = (userValue: string) => {
        setState(prev => {
            const newSelected = new Set(prev.usersForProjectSelected);
            if (newSelected.has(userValue)) {
                newSelected.delete(userValue);
            } else {
                newSelected.add(userValue);
            }
            
            // Auto-save on toggle
            if (prev.usersForProjectProject) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/UpdateProjectUsers';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.usersForProjectProject.projectID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({
                                ...s,
                                usersForProjectDlgError: 'Error saving: ' + res.callStatusMessage,
                            }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({
                            ...s,
                            usersForProjectDlgError: 'Error saving: ' + err.toString(),
                        }));
                    });
            }
            
            return { ...prev, usersForProjectSelected: newSelected };
        });
    }

    const handleUsersForProjectFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, usersForProjectDlgFilterText: e.target.value }));
    }

    const openBackgroundImgDialog = (project: MasterProjectRow) => {
        closeRowMenu();
        setState(prev => ({
            ...prev,
            backgroundImgDlgOpen: true,
            backgroundImgDlgProject: project,
            backgroundImgDlgLoading: false,
            backgroundImgDlgError: '',
        }));
    }

    const closeBackgroundImgDialog = () => {
        setState(prev => ({
            ...prev,
            backgroundImgDlgOpen: false,
            backgroundImgDlgProject: null,
            backgroundImgDlgLoading: false,
            backgroundImgDlgError: '',
        }));
    }

    const handleBackgroundImgUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !state.backgroundImgDlgProject) return;

        const project = state.backgroundImgDlgProject;
        const reader = new FileReader();
        reader.onload = () => {
            const base64String = reader.result as string;

            const updatedProject = { ...project, backgroundImg: base64String };
            setState(prev => ({
                ...prev,
                data: prev.data.map(item =>
                    item.projectID === project.projectID
                        ? updatedProject
                        : item
                ),
                backgroundImgDlgProject: updatedProject,
            }));

            handleSave(updatedProject);
        };
        reader.readAsDataURL(file);
    }

    const downloadBackgroundImg = () => {
        const project = state.backgroundImgDlgProject;
        if (!project || !project.backgroundImg) return;

        const binaryString = atob(project.backgroundImg);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${project.projectCode}_background.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const removeBackgroundImg = () => {
        const project = state.backgroundImgDlgProject;
        if (!project) return;

        const updatedProject = { ...project, backgroundImg: null };
        setState(prev => ({
            ...prev,
            data: prev.data.map(item =>
                item.projectID === project.projectID
                    ? updatedProject
                    : item
            ),
            backgroundImgDlgProject: updatedProject,
        }));

        handleSave(updatedProject);
    }



    const submitNewMasterProject = () => {
        const {
            newMasterProjectDlgCode,
            newMasterProjectDlgName,
            newMasterProjectDlgDesc,
            newMasterProjectDlgDataFolder,
            newMasterProjectDlgSqlDatabaseName,
            newMasterProjectDlgSqlDatabaseServer,
            newMasterProjectDlgIsActive,
            newMasterProjectDlgImporter,
            newMasterProjectDlgSearchQueue,
            newMasterProjectDlgInventoryScans,
            newMasterProjectDlgApiUrl,
            newMasterProjectDlgBgOpacity,
            newMasterProjectDlgTimeZone,
        } = state;
        setState(prev => ({ ...prev, newMasterProjectDlgSaving: true, newMasterProjectDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMasterProject';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                projectCode: newMasterProjectDlgCode,
                projectName: newMasterProjectDlgName,
                projectDesc: newMasterProjectDlgDesc,
                projectDataFolder: newMasterProjectDlgDataFolder,
                sqlDatabaseName: newMasterProjectDlgSqlDatabaseName,
                sqlDatabaseServer: newMasterProjectDlgSqlDatabaseServer,
                isActive: newMasterProjectDlgIsActive,
                runTcBImporterService: newMasterProjectDlgImporter,
                runTcBSearchQueue: newMasterProjectDlgSearchQueue,
                processInventoryScans: newMasterProjectDlgInventoryScans,
                apiUrl: newMasterProjectDlgApiUrl,
                backgroundImgOpacity: newMasterProjectDlgBgOpacity,
                databaseTimeZone: newMasterProjectDlgTimeZone,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, newMasterProjectDlgOpen: false, newMasterProjectDlgSaving: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, newMasterProjectDlgSaving: false, newMasterProjectDlgError: 'Error creating MasterProject: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, newMasterProjectDlgOpen: false, newMasterProjectDlgSaving: false }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, newMasterProjectDlgSaving: false, newMasterProjectDlgError: 'Error creating MasterProject: ' + err.toString() }));
            });
    }

    const IDCell = (props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.selectedIds.has(props.dataItem.projectID);
        return (
            <td
                className={`tmi-dialog-grid-center-cell ${isSelected && !inEdit ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.projectID, e)}
                style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    backgroundColor: inEdit && !isSelected ? '#f5f5f5' : undefined,
                    color: inEdit && !isSelected ? '#999' : undefined,
                    fontStyle: inEdit && !isSelected ? 'italic' : 'normal',
                    cursor: 'pointer',
                }}
                title={String(val)}
            >
                {val}
            </td>
        );
    }

    const BooleanCell = (props: GridCellProps) => {
        const value: boolean = props.dataItem[props.field!] ?? false;
        const inEdit: boolean = props.dataItem[EDIT_FIELD];
        const isSelected = state.selectedIds.has(props.dataItem.projectID);

        if (inEdit) {
            return (
                <td
                    className={`tmi-dialog-grid-center-cell ${isSelected && !inEdit ? 'tmi-dialog-grid-cell-selected' : ''}`}
                    style={{ cursor: 'pointer' }}
                >
                    <input
                        type="checkbox"
                        defaultChecked={value}
                        onKeyDown={e => handleEditorKeyDown(e, props.dataItem as MasterProjectRow)}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onChange={e => {
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.projectID === props.dataItem.projectID ? { ...item, [props.field!]: e.target.checked } : item) }));
                        }}
                    />
                </td>
            );
        }

        return (
            <td
                className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(props.dataItem.projectID, e)}
                style={{ cursor: 'pointer' }}
            >
                {value ? '✓' : ''}
            </td>
        );
    }

    const HamburgerHeaderCell = () => {
        return (
            <th className="tmi-header-action-cell tmi-command-column">
                <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={fetchData} disabled={loading} title="Refresh">
                    <span className="k-icon k-font-icon k-i-reload" />
                </button>
            </th>
        );
    }

    const CommandHeaderCell = () => {
        return (
            <th className="tmi-command-header-cell tmi-command-column">
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                    title="New Master Project"
                    onClick={openNewMasterProjectDlg}
                >
                    <span className="k-icon k-font-icon k-i-plus" />
                </button>
            </th>
        );
    }

    const CommandCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        // Simple inline command buttons (no overlay)
        return (
            <td className="tmi-command-column tmi-command-cell">
                {inEdit ? (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.projectID, e); handleCancel(); }} title="Cancel">
                            <span className="k-icon k-font-icon k-i-cancel" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.projectID, e); openDeleteConfirm(dataItem as MasterProjectRow); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.projectID, e); handleEnterEdit(dataItem); }} title="Edit">
                            <span className="k-icon k-font-icon k-i-edit" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.projectID, e); openDeleteConfirm(dataItem as MasterProjectRow); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                )}
            </td>
        );
    }

    const BackgroundImageCell = (props: GridCellProps) => {
        const hasImage = !!props.dataItem.backgroundImg;
        return (
            <td style={{ textAlign: 'center', padding: '2px' }} title={hasImage ? 'Has background image' : 'No background image'}>
                {hasImage && <span className="k-icon k-font-icon k-i-image" style={{ color: '#0066cc', fontSize: '16px' }} />}
            </td>
        );
    }

    // Shared editable text cell used by all free-text columns (Code/Name/Description/Data
    // Folder/SQL DB Name/SQL DB Server/API URL/DB Time Zone). It is memoized with useCallback and
    // reads `selectedIds` via stateRef (rather than closing over `state` directly) so its
    // identity stays stable across renders; otherwise Kendo's Grid would treat it as a "new" cell
    // renderer on every keystroke and remount the <input>, dropping focus after one character.
    const TextEditCell = useCallback((props: GridCellProps) => {
        const { dataItem, field } = props;
        const isSelected = stateRef.current.selectedIds.has(dataItem.projectID);
        const isEditing = dataItem[EDIT_FIELD] === true;
        const val = dataItem[field!] ?? '';

        if (isEditing) {
            return (
                <td className="tmi-dialog-grid-text-cell" style={{ cursor: 'pointer', padding: '2px' }}>
                    <input
                        type="text"
                        value={val}
                        onKeyDown={e => handleEditorKeyDown(e, dataItem as MasterProjectRow)}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setState(prev => ({ ...prev, data: prev.data.map(item => item.projectID === dataItem.projectID ? { ...item, [field!]: e.target.value } : item) }))}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', padding: '4px', boxSizing: 'border-box' }}
                    />
                </td>
            );
        }

        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={e => handleRowClick(dataItem.projectID, e)}
                style={{ cursor: 'pointer' }}
                title={String(val)}
            >
                {val}
            </td>
        );
    }, [handleEditorKeyDown, handleRowClick]);

    const NumberEditCell = useCallback((props: GridCellProps) => {
        const { dataItem, field } = props;
        const isSelected = stateRef.current.selectedIds.has(dataItem.projectID);
        const isEditing = dataItem[EDIT_FIELD] === true;
        const val = dataItem[field!] ?? '';

        if (isEditing) {
            return (
                <td className="tmi-dialog-grid-text-cell" style={{ cursor: 'pointer', padding: '2px' }}>
                    <input
                        type="number"
                        min="0"
                        max="100"
                        value={val}
                        onKeyDown={e => handleEditorKeyDown(e, dataItem as MasterProjectRow)}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setState(prev => ({ ...prev, data: prev.data.map(item => item.projectID === dataItem.projectID ? { ...item, [field!]: e.target.value } : item) }))}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', padding: '4px', boxSizing: 'border-box' }}
                    />
                </td>
            );
        }

        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={e => handleRowClick(dataItem.projectID, e)}
                style={{ cursor: 'pointer' }}
                title={String(val)}
            >
                {val}
            </td>
        );
    }, [handleEditorKeyDown, handleRowClick]);

    const HamburgerCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        return (
            <td style={{ textAlign: 'center', padding: '2px' }}>
                {inEdit ? (
                    <button
                        className="k-button k-button-sm k-rounded-md k-button-solid-success"
                        title="Save"
                        onClick={(e) => { handleRowClick(dataItem.projectID, e); handleSave(dataItem); }}
                    >
                        <span className="k-icon k-font-icon k-i-check" />
                    </button>
                ) : (
                    <button
                        className="k-button k-button-sm k-rounded-md"
                        title="Actions"
                        onClick={e => {
                            e.stopPropagation();
                            openRowMenu(dataItem as MasterProjectRow, e.currentTarget);
                        }}
                    >
                        <span className="k-icon k-font-icon k-i-more-vertical" />
                    </button>
                )}
            </td>
        );
    }

    const {
        data,
        loading,
        errorMsg,
        sort,
        filterTexts,
        editItemId,
        selectedIds,
    } = state;

    // Filter data manually like ImportTransactions does - NOT using Kendo's filterBy
    const filters = filterTexts || {};
    const filteredData = Object.keys(filters).length === 0 || Object.values(filters).every(v => !v)
        ? data
        : data.filter(item => {
            return Object.entries(filters).every(([f, q]) => {
                if (!q) return true;
                const val = (item as any)[f];
                const ql = q.trim().toLowerCase();
                if (ql === 'true' || ql === 'false') {
                    return String(val ?? '').toLowerCase() === ql;
                }
                return String(val ?? '').toLowerCase().includes(ql);
            });
        });

    const sortedData = orderBy(filteredData, sort);

    const gridData = sortedData.map(item => ({
        ...item,
        [EDIT_FIELD]: item.projectID === editItemId,
        [SELECTED_FIELD]: selectedIds.has(item.projectID),
    }));

    // Cache header cell renderers in ref (like ImportTransactions does with this.headerCellRenderers)
    const headerCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const HEADER_RENDERER_VERSION = 'v2';

    // Build filterable text header cell renderer - stored once and reused
    const buildHeaderCellRenderer = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_text_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                return (
                    <th className={`tmi-header-filter ${sortDir === 'asc' ? 'sort-asc' : sortDir === 'desc' ? 'sort-desc' : 'sort-none'}`} onClick={(e) => handleHeaderSortClick(field, e)}>
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onClick={(e) => { e.stopPropagation(); }}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                        <div className="tmi-header-sort-zone" onClick={() => toggleSort(field)} />
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    // Build boolean header cell renderer - stored once and reused
    const buildBooleanHeaderCellRenderer = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_bool_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                return (
                    <th className={`tmi-header-filter ${sortDir === 'asc' ? 'sort-asc' : sortDir === 'desc' ? 'sort-desc' : 'sort-none'}`} onClick={(e) => handleHeaderSortClick(field, e)}>
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <select
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e as unknown as React.ChangeEvent<HTMLInputElement>)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onClick={(e) => { e.stopPropagation(); }}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            >
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                        <div className="tmi-header-sort-zone" onClick={() => toggleSort(field)} />
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    const createSimpleHeaderCell = (field: string, title: string) => {
        const cacheKey = `${HEADER_RENDERER_VERSION}_simple_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field) ? (stateRef.current.sort[0].dir as string) : undefined;
                const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSort(field);
                };
                const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                        <div onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '4px 6px',
                            boxSizing: 'border-box',
                            fontSize: '12px',
                            lineHeight: '1.3',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            flex: 1,
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}>
                            <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                            <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                {loading && <span className="k-icon k-font-icon k-i-loading" />}
            </div>
            {errorMsg && <div style={{ color: 'red', marginBottom: '8px' }}>{errorMsg}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                <strong>Global MFA Settings</strong>
                {state.globalMfaSettingsLoading && <span className="k-icon k-font-icon k-i-loading" />}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Challenge Interval (Days)
                    <input
                        type="number"
                        className="k-input k-input-sm"
                        value={state.globalMfaChallengeIntervalDays}
                        onChange={e => setState(prev => ({ ...prev, globalMfaChallengeIntervalDays: e.target.value === '' ? '' : parseInt(e.target.value, 10) }))}
                        style={{ width: '80px' }}
                    />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Code Expiry (Minutes)
                    <input
                        type="number"
                        className="k-input k-input-sm"
                        value={state.globalMfaCodeExpiryMinutes}
                        onChange={e => setState(prev => ({ ...prev, globalMfaCodeExpiryMinutes: e.target.value === '' ? '' : parseInt(e.target.value, 10) }))}
                        style={{ width: '80px' }}
                    />
                </label>
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                    onClick={saveGlobalMfaSettings}
                    disabled={state.globalMfaSettingsSaving}
                >
                    {state.globalMfaSettingsSaving ? <span className="k-icon k-font-icon k-i-loading" /> : 'Save'}
                </button>
                {state.globalMfaSettingsError && <span style={{ color: 'red' }}>{state.globalMfaSettingsError}</span>}
            </div>
            <Grid
                className="tmi-grid MasterProjects-grid"
                style={{ flex: 1, minHeight: 0 }}
                data={gridData}
                dataItemKey={DATA_ITEM_KEY}
                resizable
                editField={EDIT_FIELD}
                onItemChange={handleItemChange}
            >
                <GridColumn headerCell={HamburgerHeaderCell} cell={HamburgerCell} width={40} filterable={false} sortable={false} headerClassName="wrap-header" />
                <GridColumn cell={IDCell} field='projectID' title='ID' filterable={false} sortable width={60} headerCell={createSimpleHeaderCell('projectID', 'ID')} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='projectCode' title='Code' headerCell={buildHeaderCellRenderer('projectCode', 'Code')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='projectName' title='Name' headerCell={buildHeaderCellRenderer('projectName', 'Name')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='projectDesc' title='Description' headerCell={buildHeaderCellRenderer('projectDesc', 'Description')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='projectDataFolder' title='Data Folder' headerCell={buildHeaderCellRenderer('projectDataFolder', 'Data Folder')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='sqlDatabaseName' title='SQL DB Name' headerCell={buildHeaderCellRenderer('sqlDatabaseName', 'SQL DB Name')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='sqlDatabaseServer' title='SQL DB Server' headerCell={buildHeaderCellRenderer('sqlDatabaseServer', 'SQL DB Server')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='isActive' title='Active' cell={BooleanCell} width={80} headerCell={buildBooleanHeaderCellRenderer('isActive', 'Active')} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='runTcBImporterService' title='Importer' cell={BooleanCell} width={80} headerCell={buildBooleanHeaderCellRenderer('runTcBImporterService', 'Importer')} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='runTcBSearchQueue' title='Search Queue' cell={BooleanCell} width={80} headerCell={buildBooleanHeaderCellRenderer('runTcBSearchQueue', 'Search Queue')} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='processInventoryScans' title='Inventory Scans' cell={BooleanCell} width={80} headerCell={buildBooleanHeaderCellRenderer('processInventoryScans', 'Inventory Scans')} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='apiUrl' title='API URL' headerCell={buildHeaderCellRenderer('apiUrl', 'API URL')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn cell={BackgroundImageCell} width={50} filterable={false} sortable={false} title="Img" headerCell={createSimpleHeaderCell('Image', 'Img')} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='backgroundImgOpacity' title='Background Image Opacity' width={120} headerCell={buildHeaderCellRenderer('backgroundImgOpacity', 'Background Image Opacity')} cell={NumberEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field='databaseTimeZone' title='DB Time Zone' headerCell={buildHeaderCellRenderer('databaseTimeZone', 'DB Time Zone')} cell={TextEditCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn headerCell={CommandHeaderCell} cell={CommandCell} width={80} filterable={false} sortable={false} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
            </Grid>

            {state.menuDataItem && (
                <div
                    ref={menuRef}
                    onFocus={onMenuFocus}
                    onBlur={onMenuBlur}
                    style={{
                        position: 'fixed',
                        left: state.menuPosition ? `${state.menuPosition.left}px` : '-9999px',
                        top: state.menuPosition ? `${state.menuPosition.top}px` : '-9999px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '200px',
                    }}
                >
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => openNewProjectBasedOn(state.menuDataItem as MasterProjectRow)}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        New Project Based on this
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => openUsersForProjectDialog(state.menuDataItem as MasterProjectRow)}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Users For Project
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => openBackgroundImgDialog(state.menuDataItem as MasterProjectRow)}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Background Image
                    </div>
                </div>
            )}

            {state.deleteConfirmItem && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete <strong>{state.deleteConfirmItem?.projectDesc}</strong>?
                    </p>
                    {state.deleteErrorMsg && (
                        <div style={{ color: 'red', marginBottom: '8px' }}>{state.deleteErrorMsg}</div>
                    )}
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeDeleteConfirm} disabled={state.deleteInProgress}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmDelete}
                            disabled={state.deleteInProgress}
                        >
                            {state.deleteInProgress ? <span className="k-icon k-font-icon k-i-loading" /> : 'Delete'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.newMasterProjectDlgOpen && (
                <Dialog
                    title="New MasterProject"
                    onClose={closeNewMasterProjectDlg}
                    width={480}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', maxHeight: '500px', overflowY: 'auto' }}>
                        {state.newMasterProjectDlgError && (
                            <div style={{ color: 'red' }}>{state.newMasterProjectDlgError}</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Code</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgCode}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgCode: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Name</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgName}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgName: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Description</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgDesc}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgDesc: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Data Folder</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgDataFolder}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgDataFolder: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>SQL Database Name</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgSqlDatabaseName}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgSqlDatabaseName: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>SQL Database Server</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgSqlDatabaseServer}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgSqlDatabaseServer: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>API URL</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgApiUrl}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgApiUrl: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Background Image Opacity</label>
                            <input
                                type="number"
                                className="k-input k-input-md k-rounded-md"
                                min="0"
                                max="100"
                                step="1"
                                value={state.newMasterProjectDlgBgOpacity}
                                onChange={e => {
                                    let val = parseInt(e.target.value, 10) || 0;
                                    val = Math.max(0, Math.min(100, val));
                                    setState(prev => ({ ...prev, newMasterProjectDlgBgOpacity: val }));
                                }}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>DB Time Zone</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newMasterProjectDlgTimeZone}
                                onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgTimeZone: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #e0e0e0', paddingTop: '8px' }}>
                            <label style={{ fontWeight: 'bold' }}>Features</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterProjectDlgIsActive}
                                    onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgIsActive: e.target.checked }))}
                                />
                                <label>Active</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterProjectDlgImporter}
                                    onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgImporter: e.target.checked }))}
                                />
                                <label>Run TcB Importer Service</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterProjectDlgSearchQueue}
                                    onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgSearchQueue: e.target.checked }))}
                                />
                                <label>Run TcB Search Queue</label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={state.newMasterProjectDlgInventoryScans}
                                    onChange={e => setState(prev => ({ ...prev, newMasterProjectDlgInventoryScans: e.target.checked }))}
                                />
                                <label>Process Inventory Scans</label>
                            </div>
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeNewMasterProjectDlg} disabled={state.newMasterProjectDlgSaving}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={submitNewMasterProject}
                            disabled={state.newMasterProjectDlgSaving}
                        >
                            {state.newMasterProjectDlgSaving ? <span className="k-icon k-font-icon k-i-loading" /> : 'Create'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.usersForProjectDlgOpen && state.usersForProjectProject && (
                <Dialog
                    title="Users For Project"
                    onClose={closeUsersForProjectDialog}
                    width={420}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                        <input
                            type="text"
                            placeholder="Filter users..."
                            value={state.usersForProjectDlgFilterText}
                            onChange={handleUsersForProjectFilterChange}
                            className="tmi-dialog-list-filter"
                        />
                        <div className="tmi-dialog-list-container">
                            {state.usersForProjectDlgError && (
                                <div className="tmi-dialog-list-error">
                                    {state.usersForProjectDlgError}
                                </div>
                            )}
                            {state.usersForProjectDlgLoading && (
                                <div className="tmi-dialog-list-loading">
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    <span>Loading users...</span>
                                </div>
                            )}
                            {!state.usersForProjectDlgLoading && state.usersDropdown.length === 0 && (
                                <div className="tmi-dialog-list-empty">No users available</div>
                            )}
                            {!state.usersForProjectDlgLoading && state.usersDropdown
                                .filter(user => user.display.toLowerCase().includes(state.usersForProjectDlgFilterText.toLowerCase()))
                                .map((user) => (
                                    <div key={user.value} className="tmi-dialog-list-item">
                                        <input
                                            type="checkbox"
                                            checked={state.usersForProjectSelected.has(user.value)}
                                            onChange={() => toggleUserSelection(user.value)}
                                            id={`user-${user.value}`}
                                        />
                                        <label htmlFor={`user-${user.value}`}>
                                            {user.display}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeUsersForProjectDialog}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.backgroundImgDlgOpen && state.backgroundImgDlgProject && (
                <Dialog
                    title={`Background Image for ${state.backgroundImgDlgProject.projectCode}`}
                    onClose={closeBackgroundImgDialog}
                    width={480}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0' }}>
                        {state.backgroundImgDlgError && (
                            <div style={{ color: 'red', padding: '8px', backgroundColor: '#fee', borderRadius: '4px' }}>
                                {state.backgroundImgDlgError}
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontWeight: 'bold' }}>Current Image</label>
                            {state.backgroundImgDlgProject.backgroundImg ? (
                                <div style={{
                                    width: '100%',
                                    height: '200px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src={state.backgroundImgDlgProject.backgroundImg.startsWith('data:')
                                            ? state.backgroundImgDlgProject.backgroundImg
                                            : `data:image/png;base64,${state.backgroundImgDlgProject.backgroundImg}`}
                                        alt="Background"
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                </div>
                            ) : (
                                <div style={{
                                    width: '100%',
                                    height: '200px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#999'
                                }}>
                                    No image uploaded
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <label style={{ flex: 1 }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleBackgroundImgUpload}
                                    disabled={state.backgroundImgDlgLoading}
                                    style={{ display: 'none' }}
                                />
                                <button
                                    className="k-button k-button-md k-rounded-md k-button-solid-primary"
                                    onClick={(e) => {
                                        (e.currentTarget.parentElement?.querySelector('input[type="file"]') as HTMLInputElement)?.click();
                                    }}
                                    disabled={state.backgroundImgDlgLoading}
                                >
                                    {state.backgroundImgDlgLoading ? <span className="k-icon k-font-icon k-i-loading" /> : 'Upload Image'}
                                </button>
                            </label>
                            <button
                                className="k-button k-button-md k-rounded-md"
                                onClick={downloadBackgroundImg}
                                disabled={!state.backgroundImgDlgProject.backgroundImg || state.backgroundImgDlgLoading}
                            >
                                Download
                            </button>
                            <button
                                className="k-button k-button-md k-rounded-md k-button-solid-error"
                                onClick={removeBackgroundImg}
                                disabled={!state.backgroundImgDlgProject.backgroundImg || state.backgroundImgDlgLoading}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeBackgroundImgDialog}
                            disabled={state.backgroundImgDlgLoading}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
}

export default connector(MasterProjects);
